import { useCallback, useMemo, useRef, useState } from 'react';
import { checkVideosIntermediateStatus } from '@bingads-webui-campaign-react/asset-library-model';
import _ from 'underscore';

import {
  transformVideoResponse,
  VideoChangedFieldsWithStatusUpdate
} from '../repository/asset-response-transformers/video-response-transformer';

export const useCommonVideoRepoMethods = ({
  repository,
  videoRepository,
  logger,
  i18n,
}) => {
  const latestTimestampRef = useRef(0);
  const [videoUpdated, setVideoUpdated] = useState({}); // sample: { 123: { .. } }
  const getVideoTaskStatus = useCallback((videoTasks) => {
    const requestTimestamp = Date.now();
    const videosToCheck = _.keys(videoTasks);

    return repository.updateVideoStatusByIds(videosToCheck).then((response) => {
      // if multipe requests are sent out, ensure to keep the result from the latest request
      let notFinishedTaskList = {};
      let newVideoUpdated = {};
      if (requestTimestamp > latestTimestampRef.current) {
        const responseKeys = [];

        response.forEach((item) => {
          responseKeys.push(item.IdString);
          const newItem = transformVideoResponse(item, i18n);
          newVideoUpdated = {
            ...newVideoUpdated,
            [item.IdString]: _.pick(newItem, VideoChangedFieldsWithStatusUpdate),
          };

          if (newItem && checkVideosIntermediateStatus(newItem)) {
            notFinishedTaskList = { ...notFinishedTaskList, [newItem.id]: newItem.status };
          }
        });

        // eslint-disable-next-line no-param-reassign
        latestTimestampRef.current = requestTimestamp;
      }

      setVideoUpdated(newVideoUpdated);

      const taskCount = Object.keys(notFinishedTaskList).length;

      return { totalCount: taskCount };
    }).catch(() => {
      // log and ignore the error
      logger.logError('Failed to get videos task status.');
    });
  }, [logger, repository, i18n]);

  const addVideoCreative = useCallback(({ videoCreative }) => {
    const promise = videoRepository.addVideoCreative({ videoCreative });
    return promise;
  }, [videoRepository]);

  const getVideo = useCallback(({ id, fetchMetadata }) => {
    const promise = videoRepository.getVideo({ id, fetchMetadata });
    return promise;
  }, [videoRepository]);

  const returnVal = useMemo(() => ({
    getVideoTaskStatus,
    videoUpdated,
    addVideoCreative,
    getVideo,
  }), [addVideoCreative, getVideo, getVideoTaskStatus, videoUpdated]);

  return returnVal;
};
